import { ChildResourceType } from "permissions/resources/permission-resource-types";

export const additionalPermissionFilterParameterNames = [
  "hasParent",
  "categories",
  "granteeUserIds",
  "accessType",
  "documentIds",
  "forumIds",
  "proposalIds",
  "workAgreementIds",
] as const;

export const targetPermissionFilterParameterNames = [
  "documentIds",
  "forumIds",
  "proposalIds",
  "workAgreementIds",
] as const;

const unsupportedPermissionFilterParameterNames = ["name"] as const;

const drawerControlledPermissionFilterParameterNames = [
  "categories",
  "granteeUserIds",
  "accessType",
] as const;

type AdditionalPermissionFilters = {
  categories: readonly string[];
  granteeUserIds: readonly string[];
  accessType: string | null;
};

export function clearAdditionalPermissionFilters(search: string): string {
  const params = new URLSearchParams(search);

  for (const key of additionalPermissionFilterParameterNames) {
    params.delete(key);
  }
  params.set("pageIndex", "0");

  return params.toString();
}

export function writeAdditionalPermissionFilters(search: string, filters: AdditionalPermissionFilters): string {
  const params = new URLSearchParams(search);

  for (const key of drawerControlledPermissionFilterParameterNames) {
    params.delete(key);
  }

  const focusedGranteeUserId = params.get("granteeUserId");
  const keepsFocusedGrantee =
    focusedGranteeUserId !== null &&
    filters.granteeUserIds.length === 1 &&
    filters.granteeUserIds[0] === focusedGranteeUserId;
  if (!keepsFocusedGrantee) params.delete("granteeUserId");

  for (const category of filters.categories) {
    params.append("categories", category);
  }

  if (!keepsFocusedGrantee) {
    for (const granteeUserId of filters.granteeUserIds) {
      params.append("granteeUserIds", granteeUserId);
    }
  }

  if (filters.accessType) {
    params.set("accessType", filters.accessType);
  }

  params.set("view", "details");
  params.set("pageIndex", "0");

  return params.toString();
}

export function hasTargetPermissionFilters(search: string | URLSearchParams): boolean {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  return targetPermissionFilterParameterNames.some((key) => params.has(key));
}

/**
 * The permissions service only stores target IDs, not a searchable display
 * name. Preserve supported ID filters while dropping stale name-search links.
 */
export function removeUnsupportedPermissionFilters(search: string): string {
  const params = new URLSearchParams(search);
  for (const key of unsupportedPermissionFilterParameterNames) {
    params.delete(key);
  }
  return params.toString();
}

export function normalizePermissionAccessType(accessType: string | null): string | null {
  if (!accessType) return null;

  const compactAccessType = accessType.trim().replaceAll(" ", "");
  const resourceType = compactAccessType.endsWith("Access") ? compactAccessType : `${compactAccessType}Access`;
  return (
    Object.values(ChildResourceType).find(
      (candidate) => candidate.toLowerCase() === resourceType.toLowerCase(),
    ) ?? null
  );
}
